import { useCallback, useEffect, useMemo, useState } from 'react';
import { trackGameComplete, trackGameRestart } from '../utils/analytics';
import './GameStyles.css';

const DIFFICULTIES = {
  easy: { pairs: 6, label: 'Easy' },
  medium: { pairs: 8, label: 'Medium' },
  hard: { pairs: 12, label: 'Hard' },
};

const SYMBOLS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L'];

const createDeck = (difficulty) => {
  const values = SYMBOLS.slice(0, DIFFICULTIES[difficulty].pairs);
  return [...values, ...values]
    .map((value, index) => ({ id: `${value}-${index}`, value, matched: false }))
    .sort(() => Math.random() - 0.5);
};

const MemoryBloom = ({ game }) => {
  const [difficulty, setDifficulty] = useState('easy');
  const [cards, setCards] = useState(() => createDeck('easy'));
  const [flipped, setFlipped] = useState([]);
  const [moves, setMoves] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [complete, setComplete] = useState(false);

  const restart = useCallback((nextDifficulty = difficulty) => {
    setDifficulty(nextDifficulty);
    setCards(createDeck(nextDifficulty));
    setFlipped([]);
    setMoves(0);
    setSeconds(0);
    setComplete(false);
    trackGameRestart(game);
  }, [difficulty, game]);

  useEffect(() => {
    if (complete) {
      return undefined;
    }

    const timer = setInterval(() => setSeconds((value) => value + 1), 1000);
    return () => clearInterval(timer);
  }, [complete]);

  useEffect(() => {
    if (cards.length > 0 && cards.every((card) => card.matched) && !complete) {
      setComplete(true);
      trackGameComplete(game, { moves, seconds, difficulty });
    }
  }, [cards, complete, difficulty, game, moves, seconds]);

  useEffect(() => {
    if (flipped.length !== 2) {
      return undefined;
    }

    const [first, second] = flipped;
    const timer = setTimeout(() => {
      if (cards[first].value === cards[second].value) {
        setCards((currentCards) =>
          currentCards.map((card, index) =>
            index === first || index === second ? { ...card, matched: true } : card
          )
        );
      }
      setFlipped([]);
    }, 650);

    return () => clearTimeout(timer);
  }, [cards, flipped]);

  const flipCard = (index) => {
    if (flipped.length >= 2 || flipped.includes(index) || cards[index].matched || complete) {
      return;
    }

    if (flipped.length === 0) {
      setFlipped([index]);
      return;
    }

    setFlipped((current) => [...current, index]);
    setMoves((value) => value + 1);
  };

  const boardClass = useMemo(() => `memory-board ${difficulty}`, [difficulty]);

  return (
    <div className="playable-game">
      <div className="difficulty-row" aria-label="Difficulty">
        {Object.entries(DIFFICULTIES).map(([key, option]) => (
          <button
            className={`difficulty-button ${difficulty === key ? 'active' : ''}`}
            key={key}
            onClick={() => restart(key)}
            type="button"
          >
            {option.label}
          </button>
        ))}
      </div>

      <div className="game-toolbar">
        <div className="game-stats">
          <span className="stat-pill">Moves {moves}</span>
          <span className="stat-pill">Time {seconds}s</span>
        </div>
        <div className="game-actions">
          <button className="game-action-button" onClick={() => restart()} type="button">
            Restart
          </button>
        </div>
      </div>

      {complete && <div className="game-status">Board cleared in {moves} moves. Nice memory run.</div>}

      <div className={boardClass} aria-label="Memory Bloom board">
        {cards.map((card, index) => {
          const revealed = flipped.includes(index) || card.matched;
          return (
            <button
              className={`memory-card ${revealed ? 'revealed' : ''} ${card.matched ? 'matched' : ''}`}
              key={card.id}
              onClick={() => flipCard(index)}
              type="button"
            >
              {revealed ? card.value : ''}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default MemoryBloom;
